import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import {
  Document,
  HeadingLevel,
  Packer,
  PageOrientation,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
} from 'docx';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { ExitIcon } from '@/assets/icons';
import { MarveriIcon } from '@/pages/overview/common/MarveriIcon';
import { ScrollableDiv } from '@/pages/overview/common/ScrollableDiv';
import { useOverview } from '@/pages/overview/common/utils';
import { MAClause, MaClausesData } from '@/pages/overview/contract-clauses/common/utils';

const mixedParagraph = (boldText: string, normalText?: string): Paragraph =>
  new Paragraph({
    children: [
      new TextRun({ text: boldText, bold: true }),
      ...(normalText ? [new TextRun({ text: `: ${normalText}` })] : []),
    ],
  });

/**
 * Utility to create a bold/regular paragraph.
 */
const paragraph = (text: string, bold = false) =>
  new Paragraph({ children: [new TextRun({ text, bold })] });

interface ExportAsWordModalProps {
  tableData: MaClausesData[];
  isWordExportModalOpen: boolean;
  setIsWordExportModalOpen: (isOpen: boolean) => void;
  columnCategoryToTitleMap: Record<string, string>;
  columnVisibility: Record<string, boolean>;
  orderedColumns: string[];
}

export const ExportAsWordModal = ({
  tableData,
  isWordExportModalOpen,
  setIsWordExportModalOpen,
  columnCategoryToTitleMap,
  columnVisibility,
  orderedColumns,
}: ExportAsWordModalProps) => {
  const [showExplanations, setShowExplanations] = useState(false);
  const [showCitations, setShowCitations] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>(['File Name']);

  const { matter } = useOverview();

  //Get the columns available for export, ensure File Name is first displayed
  const columnsToExport = useMemo(() => {
    // Keep the exact left-to-right order that came from the table
    const fromTable = orderedColumns.filter((header) => {
      // Header is really visible & (for non-File Name) is requested
      if (header === 'File Name') return true;
      const categoryKey = Object.entries(columnCategoryToTitleMap).find(
        ([, value]) => value === header,
      )?.[0];
      return categoryKey && matter.requestedDocumentAnalysisColumns.includes(categoryKey);
    });

    return fromTable;
  }, [orderedColumns, columnCategoryToTitleMap, matter.requestedDocumentAnalysisColumns]);

  const disableColumnSelection = (column: string) => {
    if (column === 'File Name') {
      return true;
    }
    if (selectedColumns.length >= 4 && !selectedColumns.includes(column)) {
      return true;
    }
    return false;
  };

  const handleColumnSelection = (checkedColumn: string) => {
    if (disableColumnSelection(checkedColumn)) {
      return;
    }
    setSelectedColumns((prevColumns) => {
      if (prevColumns.includes(checkedColumn)) {
        return prevColumns.filter((column) => column !== checkedColumn);
      }
      return [...prevColumns, checkedColumn];
    });
  };

  /**
   * Helper to return the correct value for either the response (aka "result") or summary (aka "explanation").
   */
  const getClauseField = useCallback(
    (clause: MAClause | undefined, field: 'response' | 'summary') => {
      if (!clause) return '';
      if (field === 'response') {
        return clause.userResponse?.trim() || clause.response || '';
      }
      return clause.userSummary?.trim() || clause.summary || '';
    },
    [],
  );

  /**
   * Generates and downloads the Word document.
   */
  const downloadTableAsWord = useCallback(async () => {
    try {
      // Step 1 ───── Filter columns actually exportable given visibility + requested columns
      const visibleColumns = columnsToExport.filter((column) => {
        const categoryKey = Object.entries(columnCategoryToTitleMap).find(
          ([, value]) => value === column,
        )?.[0];

        return (
          selectedColumns.includes(column) &&
          columnVisibility[column] &&
          (column === 'File Name' ||
            (categoryKey && matter.requestedDocumentAnalysisColumns.includes(categoryKey)))
        );
      });

      // Step 2 ───── Build column metadata (does this column get Explanation / Citation sub-columns?)
      interface ColumnMeta {
        title: string;
        hasExplanation: boolean;
        hasCitation: boolean;
      }

      const columnMeta: ColumnMeta[] = visibleColumns.map((title) => {
        // File Name never has sub-columns
        if (title === 'File Name') {
          return { title, hasExplanation: false, hasCitation: false };
        }

        let hasExplanation = false;
        let hasCitation = false;

        if (showExplanations || showCitations) {
          for (const row of tableData) {
            const clause = row.maClauses.find(
              (c) => columnCategoryToTitleMap[c.category] === title,
            );
            if (!clause) continue;
            if (showExplanations && (clause.summary || clause.userSummary)) hasExplanation = true;
            if (showCitations && clause.citation) hasCitation = true;
            if (hasExplanation && hasCitation) break;
          }
        }

        return { title, hasExplanation, hasCitation };
      });

      // ────────────────────────────
      // Step 3 – Build header rows
      // ────────────────────────────
      const topHeaderCells: TableCell[] = [];
      const secondHeaderCells: TableCell[] = [];

      const colWidthPct = 100 / (columnMeta.length * 2);

      /**
       * We add a second header row only if at least ONE column will
       * actually display a "Citation" sub-column.
       */
      const hasSecondHeaderRow = showCitations && columnMeta.some((c) => c.hasCitation);

      columnMeta.forEach((col) => {
        // Will THIS column show explanations / citations?
        const showExp = showExplanations && col.hasExplanation;
        const showCit = showCitations && col.hasCitation;

        /**
         * Sub-columns exist **only for citations**:
         *   • If showCit → always two sub-columns:
         *       ⟶ "Result"  (or "Result and Explanation" when showExp)
         *       ⟶ "Citation"
         *   • Otherwise → no sub-columns
         */
        const subColCount = showCit ? 2 : 0;

        // If explanations are ON *without* citations, fold them into the title
        const titleText = showExp && !showCit ? `${col.title} - Result and Explanation` : col.title;

        // ───── FILE NAME column (never gets sub-columns)
        if (col.title === 'File Name') {
          topHeaderCells.push(
            new TableCell({
              ...(hasSecondHeaderRow ? { rowSpan: 2 } : {}),
              children: [paragraph(col.title, true)], // bold
              width: { size: colWidthPct * 2, type: 'pct' },
            }),
          );
          return;
        }

        // ───── COLUMNS with NO sub-columns (simple one-cell header)
        if (subColCount === 0) {
          topHeaderCells.push(
            new TableCell({
              ...(hasSecondHeaderRow ? { rowSpan: 2 } : {}),
              children: [paragraph(titleText, true)], // bold
              width: { size: colWidthPct * 2, type: 'pct' },
            }),
          );
          return;
        }

        // ───── COLUMNS with sub-columns (citations present)
        topHeaderCells.push(
          new TableCell({
            columnSpan: subColCount,
            children: [paragraph(titleText, true)],
            width: { size: colWidthPct * 2, type: 'pct' },
          }),
        );

        // Build the sub-header cells (row 2) in the correct order
        const resultLabel = showExp ? 'Result and Explanation' : 'Result';
        secondHeaderCells.push(
          new TableCell({
            children: [paragraph(resultLabel, true)],
            width: { size: colWidthPct, type: 'pct' },
          }),
        );
        secondHeaderCells.push(
          new TableCell({
            children: [paragraph('Citation', true)],
            width: { size: colWidthPct, type: 'pct' },
          }),
        );
      });

      // ───── Assemble header rows
      const headerRows: TableRow[] = [new TableRow({ children: topHeaderCells })];
      if (hasSecondHeaderRow) {
        headerRows.push(new TableRow({ children: secondHeaderCells }));
      }

      // ────────────────────────────
      // Step 4 – Build data rows
      // ────────────────────────────
      const dataRows: TableRow[] = tableData.map((row) => {
        const cells: TableCell[] = [];

        // File Name first
        cells.push(new TableCell({ children: [paragraph(row.sourceDocument.displayName)] }));

        columnMeta.forEach((col) => {
          if (col.title === 'File Name') return; // already added

          const clause = row.maClauses.find(
            (c) => columnCategoryToTitleMap[c.category] === col.title,
          );

          const response = getClauseField(clause, 'response');
          const explanation = getClauseField(clause, 'summary');
          const allCitations =
            clause?.citations?.map((citation) => citation.key).filter(Boolean) || [];
          const citationText = allCitations.join('; \n');

          // Does THIS column actually show explanations / citations?
          const showExp = showExplanations && col.hasExplanation;
          const showCit = showCitations && col.hasCitation;

          if (showCit) {
            // ── Two-cell layout:  (Result [+ Explanation])   |   Citation
            const resultText =
              showExp && explanation ? mixedParagraph(response, explanation) : paragraph(response);

            cells.push(new TableCell({ children: [resultText] }));
            cells.push(new TableCell({ children: [paragraph(citationText)] }));
          } else {
            // ── Single-cell layout
            const cellText =
              showExp && explanation ? mixedParagraph(response, explanation) : paragraph(response);

            cells.push(new TableCell({ children: [cellText] }));
          }
        });

        return new TableRow({ children: cells });
      });

      // Step 5 ───── Assemble document
      const doc = new Document({
        sections: [
          {
            properties: {
              page: { size: { orientation: PageOrientation.LANDSCAPE } },
            },
            children: [
              new Paragraph({
                text: `${matter.client.name} Document Analysis`,
                heading: HeadingLevel.HEADING_1,
              }),
              paragraph(`Generated on ${new Date().toDateString()}`),
              new Table({
                width: { size: 100, type: 'pct' },
                rows: [...headerRows, ...dataRows],
              }),
            ],
          },
        ],
      });

      // Step 6 ───── Export
      const blob = await Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${new Date().toDateString()} ${matter.client.name} Document Analysis.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setIsWordExportModalOpen(false);
    } catch (error) {
      toast.error('Failed to export document. Please try again.');
    }
  }, [
    columnCategoryToTitleMap,
    columnVisibility,
    getClauseField,
    matter.client.name,
    matter.requestedDocumentAnalysisColumns,
    selectedColumns,
    columnsToExport,
    setIsWordExportModalOpen,
    showCitations,
    showExplanations,
    tableData,
  ]);

  return (
    <Dialog
      open={isWordExportModalOpen}
      onClose={() => setIsWordExportModalOpen(false)}
      className="fixed inset-0 z-10 flex items-center justify-center overflow-y-auto bg-black/50"
      data-testid="custom-column-wizard"
    >
      <DialogPanel className="flex min-w-[500px] flex-col gap-8 rounded-[16px] bg-[#242324] p-8">
        <div className="flex justify-between">
          <DialogTitle className="text-[24px] font-medium text-[#EAE6EA]">
            Export to Word
          </DialogTitle>

          <MarveriIcon
            icon={ExitIcon}
            onClick={() => setIsWordExportModalOpen(false)}
            iconStyle=""
            iconType="other"
          />
        </div>

        <div>
          <h3 className="mb-6 text-[14px] font-medium text-[#EAE6EA]">
            Select up to 3 additional columns.
          </h3>
          <div className="relative rounded-[8px] bg-[#171617] py-3 pr-3">
            <ScrollableDiv containerStyle="bg-[#171617] text-[#CECACE] font-medium text-[12px] max-h-[306px]">
              {columnsToExport.map((column, index) => {
                return (
                  <div key={index}>
                    <div className="flex items-center gap-2 px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selectedColumns.includes(column)}
                        onChange={() => handleColumnSelection(column)}
                        className={`${disableColumnSelection(column) ? 'cursor-not-allowed checked:bg-[url("@/assets/icons/cc-checkmark-gray.svg")]' : 'cursor-pointer checked:bg-[url("@/assets/icons/cc-checkmark-light.svg")]'} size-[20px] appearance-none rounded-[3px] border border-N300 bg-contain  bg-center bg-no-repeat checked:border-transparent`}
                        data-testid="add-column-checkbox"
                      />
                      <span>{column}</span>
                    </div>
                  </div>
                );
              })}
            </ScrollableDiv>
          </div>
          <div className="mt-4 flex items-center gap-6 text-[14px] font-medium text-[#EAE6EA]">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={showCitations}
                onChange={() => setShowCitations((current) => !current)}
                className={`size-[20px] cursor-pointer appearance-none rounded-[3px] border border-N300 bg-contain bg-center  bg-no-repeat checked:border-transparent checked:bg-[url("@/assets/icons/cc-checkmark-light.svg")]`}
              />
              <span>Citations</span>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={showExplanations}
                onChange={() => setShowExplanations((current) => !current)}
                className={`size-[20px] cursor-pointer appearance-none rounded-[3px] border border-N300 bg-contain bg-center  bg-no-repeat checked:border-transparent checked:bg-[url("@/assets/icons/cc-checkmark-light.svg")]`}
              />
              <span>Explanations</span>
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between">
            <span className="text-[14px] font-medium text-[#EAE6EA]">
              {selectedColumns.length} /4 selected
            </span>
            <div className="flex items-center gap-4">
              <div
                className="cursor-pointer text-N200 hover:text-[#EAE6EA]"
                onClick={() => setIsWordExportModalOpen(false)}
              >
                Cancel
              </div>
              <div
                className="cursor-pointer rounded-full bg-[#EAE6EA]  px-[18px] py-3 text-[14px] font-medium hover:bg-N200"
                onClick={downloadTableAsWord}
              >
                Export
              </div>
            </div>
          </div>
        </div>
      </DialogPanel>
    </Dialog>
  );
};
